import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoginFormState, ValidationError } from '../models/User';
import { AuthentificationApi, ApiUsersLoginPostRequest } from '../api/services';
import { useAuth } from '../contexts/AuthContext';

export class LoginViewModel {
  private navigate: ReturnType<typeof useNavigate>;
  private authApi: AuthentificationApi;

  constructor(navigate: ReturnType<typeof useNavigate>) {
    this.navigate = navigate;
    this.authApi = new AuthentificationApi();
  }

  // Initial state
  getInitialState(): LoginFormState {
    return {
      email: '',
      password: '',
      isLoading: false,
      errors: [],
      success: false,
      message: ''
    };
  }

  // Client-side validation
  validateForm(email: string, password: string): ValidationError[] {
    const errors: ValidationError[] = [];

    if (!email.trim()) {
      errors.push({ field: 'email', message: 'L\'adresse email est requise' });
    } else if (!this.isValidEmail(email)) {
      errors.push({ field: 'email', message: 'Format d\'email invalide' });
    }

    if (!password) {
      errors.push({ field: 'password', message: 'Le mot de passe est requis' });
    }

    return errors;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  // Login API call
  async login(email: string, password: string, authLogin: (token: string, user: any) => void): Promise<{ success: boolean; message?: string; errors?: string[] }> {
    try {
      const validationErrors = this.validateForm(email, password);
      if (validationErrors.length > 0) {
        return {
          success: false,
          errors: validationErrors.map(e => e.message)
        };
      }

      const loginData: ApiUsersLoginPostRequest = {
        email: email.trim().toLowerCase(),
        password: password
      };

      const response = await this.authApi.apiUsersLoginPost(loginData);

      if (!response.data?.token || !response.data?.user) {
        return {
          success: false,
          message: 'Une erreur est survenue lors de la connexion'
        };
      }

      // Ce back-office est réservé à l'équipe Dingo — un compte client
      // (role "user") n'a rien à y faire et n'a accès à aucune page
      // fonctionnelle ici, seule l'app mobile lui est destinée.
      const role = (response.data.user as any)?.role;
      if (role !== 'admin' && role !== 'moderator') {
        return {
          success: false,
          message: "Cet espace est réservé à l'équipe Dingo. Utilisez l'application mobile Dingo pour vous connecter."
        };
      }

      authLogin(response.data.token, response.data.user);

      return {
        success: true,
        message: 'Connexion réussie !'
      };

    } catch (error: any) {
      console.error('Erreur lors de la connexion:', error);
      // Un 401 ici n'est pas une session expirée, c'est un identifiant faux :
      // l'API répond `{ error: 'Invalid email or password' }`, en anglais et
      // sous la clé `error`, que ce bloc ne lisait pas — l'admin voyait « une
      // erreur est survenue », comme pour une panne, et cherchait ailleurs que
      // dans son clavier. Même formulation que l'app mobile.
      if (error.response?.status === 401) {
        return {
          success: false,
          message: 'E-mail ou mot de passe incorrect.'
        };
      }
      const serverMessage = error.response?.data?.message || error.response?.data?.error;
      if (serverMessage) {
        return {
          success: false,
          message: serverMessage
        };
      }
      return {
        success: false,
        message: 'Une erreur est survenue lors de la connexion'
      };
    }
  }

  // Navigate after successful login
  onLoginSuccess(): void {
    // Navigate to dashboard or home page
    this.navigate('/dashboard');
  }

  // Check if form is valid
  isFormValid(email: string, password: string): boolean {
    const errors = this.validateForm(email, password);
    return errors.length === 0;
  }
}

// React Hook to use the ViewModel
export const useLoginViewModel = () => {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [state, setState] = useState<LoginFormState>(new LoginViewModel(navigate).getInitialState());

  // Mémorisé : recréé à chaque rendu, il ne pouvait figurer dans aucune liste
  // de dépendances sans les invalider toutes — d'où deux avertissements
  // exhaustive-deps et deux rappels qui se fermaient sur une instance périmée.
  const viewModel = useMemo(() => new LoginViewModel(navigate), [navigate]);

  const updateField = useCallback((field: 'email' | 'password', value: string) => {
    setState(prevState => ({
      ...prevState,
      [field]: value,
      errors: []
    }));
  }, []);

  const handleSubmit = useCallback(async () => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: [],
      success: false,
      message: ''
    }));

    const result = await viewModel.login(state.email, state.password, authLogin);

    if (result.success) {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        success: true,
        message: result.message || 'Connexion réussie !'
      }));
      
      // Navigate after successful login
      viewModel.onLoginSuccess();
    } else {
      setState(prevState => ({
        ...prevState,
        isLoading: false,
        errors: result.errors?.map((error: string, index: number) => ({
          field: `field_${index}`,
          message: error
        })) || [],
        message: result.message || 'Erreur lors de la connexion'
      }));
    }
  }, [state.email, state.password, authLogin, viewModel]);

  const isFormValid = useCallback(() => {
    return viewModel.isFormValid(state.email, state.password);
  }, [state.email, state.password, viewModel]);

  const resetState = useCallback(() => {
    setState(new LoginViewModel(navigate).getInitialState());
  }, [navigate]);

  return {
    state,
    updateField,
    handleSubmit,
    isFormValid,
    resetState,
    setState
  };
}; 